import React, {
  useEffect,
  useImperativeHandle,
  forwardRef,
  useState,
  createContext,
  useContext,
} from 'react';

const AnalyticsEventContext = createContext<Record<string, unknown>>({});

/**
 * @deprecated Avoid adding new uses of this hook. It makes it difficult / impossible
 * to properly type all the data we ultimately send to Big Picture. We should try
 * to avoid obscuring the call to BigPictureClient.sendEvent behind layers of functions
 * so that the compiler can properly infer types. Simplicity is our friend here.
 */
export function useAnalyticsEventContext() {
  return useContext(AnalyticsEventContext);
}

export interface AnalyticsEventProviderRef {
  addEventData: (arg0: Record<string, unknown>) => void;
}

type Props = {
  children: React.ReactNode;
  eventData?: Record<string, unknown>;
};

/** Expose a provider that copies the value of parent providers context of the same type
 * and can be used to add additional data to context
 *
 * @deprecated Avoid adding new uses of this context provider. It makes it difficult / impossible
 * to properly type all the data we ultimately send to Big Picture. We should try
 * to avoid obscuring the call to BigPictureClient.sendEvent behind layers of functions
 * so that the compiler can properly infer types. Simplicity is our friend here.
 */
export const AnalyticsEventProvider = forwardRef(function AnalyticsEventProvider(
  { children, eventData }: Props,
  ref,
) {
  // Require any parent context so that it can be extended as we traverse down the tree.
  // This allows more data to be attached as it becomes available as we build out the
  // component tree.
  const parentContext = useAnalyticsEventContext();
  // Note that keys in passed `eventData` overwrite `parentContext`
  const [storedValues, setStoredValues] = useState(() => ({
    ...parentContext,
    ...eventData,
  }));

  useEffect(() => {
    setStoredValues((prevStoredValues) => ({
      ...prevStoredValues,
      ...parentContext,
      ...eventData,
    }));
  }, [JSON.stringify(eventData), JSON.stringify(parentContext)]);

  // Expose a handle so that we can imperatively add event data from async calls and effects
  useImperativeHandle(ref, () => ({
    addEventData(addedEventData: Record<string, unknown>): void {
      setStoredValues((prevStoredValues) => ({
        ...prevStoredValues,
        ...addedEventData,
      }));
    },
  }));

  return (
    <AnalyticsEventContext.Provider value={storedValues}>{children}</AnalyticsEventContext.Provider>
  );
});
